// Snap a selected part so a chosen point of its bounding box lands on a
// target (origin, another part's box, a picked point). Pure math + AST frame
// resolution; EditorApp turns the result into an undoable translate() edit.
import type { Program, Statement, ModuleCallStatement, Expression } from '../scad/ast';

export type V3 = [number, number, number];
/** Row-major 3×3 matrix. */
export type Mat3 = [number, number, number, number, number, number, number, number, number];

/** Per-axis anchor on a box: -1 = min, 0 = center, 1 = max, null = keep (don't move on this axis). */
export type AxisAnchor = -1 | 0 | 1 | null;
export type SnapAnchor = [AxisAnchor, AxisAnchor, AxisAnchor];

export interface Box3 {
  min: V3;
  max: V3;
}

export type SnapTarget =
  | { kind: 'point'; point: V3 }
  | { kind: 'box'; box: Box3; anchor: SnapAnchor };

/** Coordinate of the anchor on each axis (null where the anchor is "keep"). */
export function anchorPoint(box: Box3, anchor: SnapAnchor): Array<number | null> {
  return [0, 1, 2].map(i => {
    const a = anchor[i];
    if (a === null) return null;
    return a === -1 ? box.min[i] : a === 1 ? box.max[i] : (box.min[i] + box.max[i]) / 2;
  });
}

/**
 * World-space (SCAD) move that puts `sourceAnchor` of `source` onto the
 * target. Axes where the source anchor is "keep" don't move. For a box
 * target, an axis whose target anchor is "keep" doesn't move either.
 */
export function snapDelta(source: Box3, sourceAnchor: SnapAnchor, target: SnapTarget): V3 {
  const src = anchorPoint(source, sourceAnchor);
  const dst = target.kind === 'point' ? target.point : anchorPoint(target.box, target.anchor);
  return [0, 1, 2].map(i => {
    const s = src[i], d = dst[i];
    return s === null || d === null ? 0 : d - s;
  }) as V3;
}

// --- Frame resolution ----------------------------------------------------

/** Modules that don't change the orientation/scale of their children. */
const FRAME_NEUTRAL = new Set([
  'translate', 'color', 'render', 'union', 'difference', 'intersection', 'group',
  'hull', 'minkowski', 'align', 'position', 'echo', 'let', 'assert',
]);

const LAYOUT_CHILDREN = new Set(['position', 'align', 'attach']);

export type FrameResult = { ok: true; linear: Mat3 } | { ok: false; reason: string };

/**
 * Statements enclosing the statement with source range [from, to)
 * (outermost first), or a reason why a move there would be ambiguous.
 */
export function findAncestors(program: Program, from: number, to: number): { chain: ModuleCallStatement[] } | { reason: string } {
  const chain: ModuleCallStatement[] = [];
  // A statement starting where the target starts is the target itself
  // (end offsets can differ by trailing `;`/whitespace); any wrapper starts earlier.
  const contains = (s: Statement) => s.from.offset < from && s.to.offset >= to;
  let level: Statement[] = program.statements;
  for (;;) {
    const parent = level.find(contains);
    if (!parent) return { chain };
    switch (parent.type) {
      case 'ModuleCallStatement':
        chain.push(parent);
        level = parent.children;
        break;
      case 'IfStatement':
        level = [...parent.thenBody, ...parent.elseBody];
        break;
      case 'ForStatement':
        return { reason: 'it is inside a for loop — the move would apply to every iteration' };
      case 'ModuleDefinitionStatement':
        return { reason: `it is defined inside module ${parent.name}() — the move would apply to every instance; select the module call instead` };
      default:
        return { chain };
    }
  }
}

const IDENTITY: Mat3 = [1, 0, 0, 0, 1, 0, 0, 0, 1];

export function mul3(a: Mat3, b: Mat3): Mat3 {
  const r = new Array(9).fill(0) as Mat3;
  for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) {
    r[i * 3 + j] = a[i * 3] * b[j] + a[i * 3 + 1] * b[3 + j] + a[i * 3 + 2] * b[6 + j];
  }
  return r;
}

export function apply3(m: Mat3, v: V3): V3 {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
  ];
}

/** Solve m·x = v (Cramer's rule); null when m is singular. */
export function solve3(m: Mat3, v: V3): V3 | null {
  const det = (a: Mat3) =>
    a[0] * (a[4] * a[8] - a[5] * a[7]) - a[1] * (a[3] * a[8] - a[5] * a[6]) + a[2] * (a[3] * a[7] - a[4] * a[6]);
  const d = det(m);
  if (Math.abs(d) < 1e-12) return null;
  const col = (k: number): Mat3 => m.map((x, idx) => (idx % 3 === k ? v[Math.floor(idx / 3)] : x)) as Mat3;
  return [det(col(0)) / d, det(col(1)) / d, det(col(2)) / d];
}

const rad = (deg: number) => (deg * Math.PI) / 180;
const rotX = (a: number): Mat3 => { const c = Math.cos(rad(a)), s = Math.sin(rad(a)); return [1, 0, 0, 0, c, -s, 0, s, c]; };
const rotY = (a: number): Mat3 => { const c = Math.cos(rad(a)), s = Math.sin(rad(a)); return [c, 0, s, 0, 1, 0, -s, 0, c]; };
const rotZ = (a: number): Mat3 => { const c = Math.cos(rad(a)), s = Math.sin(rad(a)); return [c, -s, 0, s, c, 0, 0, 0, 1]; };

function axisAngle(axis: V3, deg: number): Mat3 | null {
  const l = Math.hypot(...axis);
  if (l < 1e-12) return null;
  const [x, y, z] = axis.map(v => v / l);
  const c = Math.cos(rad(deg)), s = Math.sin(rad(deg)), t = 1 - c;
  return [
    t * x * x + c, t * x * y - s * z, t * x * z + s * y,
    t * x * y + s * z, t * y * y + c, t * y * z - s * x,
    t * x * z - s * y, t * y * z + s * x, t * z * z + c,
  ];
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const asVec3 = (v: unknown, fill = 0): V3 | null => {
  if (!Array.isArray(v) || v.length < 2 || !v.slice(0, 3).every(isNum)) return null;
  return [v[0], v[1], v.length > 2 ? v[2] : fill] as V3;
};

function argOf(stmt: ModuleCallStatement, name: string, position: number): Expression | undefined {
  return stmt.args.find(a => a.name === name)?.value
    ?? (position < stmt.args.length && !stmt.args[position].name ? stmt.args[position].value : undefined);
}

/** Linear (3×3) part of one module call, OpenSCAD/Manifold semantics. */
export function linearOf(stmt: ModuleCallStatement, evalExpr: (e: Expression) => unknown): Mat3 | string {
  if (FRAME_NEUTRAL.has(stmt.name)) return IDENTITY;
  const bad = `can't resolve ${stmt.name}() arguments`;
  switch (stmt.name) {
    case 'rotate': {
      const aExpr = argOf(stmt, 'a', 0);
      const a = aExpr ? evalExpr(aExpr) : 0;
      if (isNum(a)) {
        const vExpr = argOf(stmt, 'v', 1);
        if (!vExpr) return rotZ(a);
        const v = asVec3(evalExpr(vExpr));
        return v ? axisAngle(v, a) ?? IDENTITY : bad;
      }
      const e = asVec3(a);
      return e ? mul3(rotZ(e[2]), mul3(rotY(e[1]), rotX(e[0]))) : bad;
    }
    case 'scale': {
      const vExpr = argOf(stmt, 'v', 0);
      const v = vExpr ? evalExpr(vExpr) : 1;
      if (isNum(v)) return [v, 0, 0, 0, v, 0, 0, 0, v];
      const s = asVec3(v, 1);
      return s ? [s[0], 0, 0, 0, s[1], 0, 0, 0, s[2]] : bad;
    }
    case 'mirror': {
      const vExpr = argOf(stmt, 'v', 0);
      const n = vExpr ? asVec3(evalExpr(vExpr)) : [1, 0, 0] as V3;
      if (!n) return bad;
      const l2 = n[0] * n[0] + n[1] * n[1] + n[2] * n[2];
      if (l2 < 1e-12) return IDENTITY;
      return [0, 1, 2].flatMap(i => [0, 1, 2].map(j => (i === j ? 1 : 0) - (2 * n[i] * n[j]) / l2)) as Mat3;
    }
    case 'multmatrix': {
      const mExpr = argOf(stmt, 'm', 0);
      const m = mExpr ? evalExpr(mExpr) : undefined;
      if (!Array.isArray(m) || m.length < 3) return bad;
      const rows = m.slice(0, 3).map(r => (Array.isArray(r) ? r.slice(0, 3) : []));
      if (!rows.every(r => r.length === 3 && r.every(isNum))) return bad;
      return rows.flat() as Mat3;
    }
    case 'attach':
      return 'it is placed with attach() — its frame is rotated by the layout; move the attach() call instead';
    case 'resize':
      return 'it is inside resize() — the scale depends on the result size';
    default:
      return `it is inside ${stmt.name}()`;
  }
}

/** Combined linear map local → world for a statement nested in `chain`. */
export function frameOf(chain: ModuleCallStatement[], evalExpr: (e: Expression) => unknown): FrameResult {
  const attach = chain.find(s => s.name === 'attach');
  if (attach) return { ok: false, reason: linearOf(attach, evalExpr) as string };
  const placed = chain.find(s => s.name === 'position' || s.name === 'align');
  if (placed && placed === chain[chain.length - 1]) {
    return { ok: false, reason: `it is placed by ${placed.name}() on its parent — change the anchor argument instead` };
  }
  let m: Mat3 = IDENTITY;
  for (let i = 0; i < chain.length; i++) {
    const s = chain[i];
    // A layout parent (e.g. `cube(10) { position(TOP) … }`) doesn't
    // transform its layout children — they share the parent's frame.
    if (LAYOUT_CHILDREN.has(chain[i + 1]?.name ?? '')) continue;
    const l = linearOf(s, evalExpr);
    if (typeof l === 'string') return { ok: false, reason: l };
    m = mul3(m, l);
  }
  return { ok: true, linear: m };
}
